import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { EmailTestModeBadge } from '../../components/revenue/EmailTestModeBadge';
import { useRevenueAccess } from '../../hooks/useRevenueAccess';

type TeamMemberHit = {
  id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  name: string | null;
};

type ManagerNotifyRow = {
  team_member_id: number;
  member: TeamMemberHit;
  explicit_rule: {
    notify_enabled: boolean;
    email_override: string | null;
    updated_at: string;
  } | null;
};

function effectiveNotifyEnabled(r: ManagerNotifyRow): boolean {
  return r.explicit_rule?.notify_enabled ?? true;
}

function effectiveEmailOverride(r: ManagerNotifyRow): string | null {
  return r.explicit_rule?.email_override ?? null;
}

function settingsMatchProductDefault(notify: boolean, email_override: string | null): boolean {
  return notify === true && email_override == null;
}

function displayTeamMemberName(m: TeamMemberHit | undefined): string {
  if (!m) return '';
  const fn = String(m.first_name ?? '').trim();
  const ln = String(m.last_name ?? '').trim();
  if (fn || ln) return `${fn} ${ln}`.trim();
  const n = String(m.name ?? '').trim();
  return n;
}

export default function SalesManagerNotifications() {
  const { canMutateRevenue } = useRevenueAccess();
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      <main className="flex flex-1 flex-col min-h-0 max-w-[90rem] mx-auto w-full py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex shrink-0 flex-wrap items-center gap-3">
          <Link to="/revenue-collection" className="btn-icon-back" aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="page-title">Sales manager notifications</h1>
          <EmailTestModeBadge />
        </div>

        <div className="flex flex-1 flex-col min-h-0 overflow-hidden bg-white rounded-lg shadow">
          <div className="shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              When a hotel validates its revenue form, managers with at least one booked group at <strong>0&nbsp;€</strong>{' '}
              realised receive one recap email per manager (template type &quot;Sales manager recap&quot;). Everyone with a
              BackYou account is listed below. <strong>Default</strong> means notify on using{' '}
              <span className="font-medium">team_members.email</span>.
              {canMutateRevenue ? (
                <>
                  {' '}
                  Use <strong>Reset</strong> to clear a custom rule. Edit the recap message under{' '}
                </>
              ) : (
                <> Recap message templates:{' '}</>
              )}
              <Link to="/revenue-collection/templates" className="font-medium text-action hover:underline">
                Email templates
              </Link>
              .
            </p>
          </div>
          <SalesManagerNotifyRulesSection />
        </div>
      </main>
    </div>
  );
}

function SalesManagerNotifyRulesSection() {
  const { canMutateRevenue } = useRevenueAccess();
  const [rows, setRows] = useState<ManagerNotifyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState('');
  const [overrideDrafts, setOverrideDrafts] = useState<Record<number, string>>({});

  const loadAll = async () => {
    setLoading(true);
    setSectionError(null);
    try {
      const members: TeamMemberHit[] = [];
      const pageSize = 1000;
      for (let from = 0; ; from += pageSize) {
        const { data: page, error: mErr } = await supabase
          .from('team_members')
          .select('id, email, first_name, last_name, name')
          .order('id', { ascending: true })
          .range(from, from + pageSize - 1);
        if (mErr) throw mErr;
        if (!page?.length) break;
        members.push(...(page as TeamMemberHit[]));
        if (page.length < pageSize) break;
      }

      const { data: rulesData, error: rErr } = await supabase
        .from('revenue_collection_sales_manager_notify_rules')
        .select('team_member_id, notify_enabled, email_override, updated_at');
      if (rErr) throw rErr;
      const ruleById = new Map<
        number,
        { notify_enabled: boolean; email_override: string | null; updated_at: string }
      >();
      for (const raw of rulesData ?? []) {
        const row = raw as {
          team_member_id: number;
          notify_enabled: boolean;
          email_override: string | null;
          updated_at: string;
        };
        ruleById.set(Number(row.team_member_id), {
          notify_enabled: row.notify_enabled,
          email_override: row.email_override,
          updated_at: row.updated_at,
        });
      }

      const merged: ManagerNotifyRow[] = members.map((member) => {
        const id = Number(member.id);
        const ex = ruleById.get(id);
        return {
          team_member_id: id,
          member,
          explicit_rule: ex
            ? {
                notify_enabled: ex.notify_enabled,
                email_override: ex.email_override,
                updated_at: ex.updated_at,
              }
            : null,
        };
      });

      merged.sort((a, b) => {
        const na = (displayTeamMemberName(a.member) || `id ${a.team_member_id}`).toLowerCase();
        const nb = (displayTeamMemberName(b.member) || `id ${b.team_member_id}`).toLowerCase();
        return na.localeCompare(nb, undefined, { sensitivity: 'base' });
      });

      setRows(merged);
      const drafts: Record<number, string> = {};
      for (const x of merged) {
        drafts[x.team_member_id] = effectiveEmailOverride(x) ?? '';
      }
      setOverrideDrafts(drafts);
    } catch (e) {
      setSectionError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, []);

  const persistRule = async (
    teamMemberId: number,
    notify_enabled: boolean,
    email_override: string | null,
  ) => {
    if (!canMutateRevenue) return;
    const now = new Date().toISOString();
    if (settingsMatchProductDefault(notify_enabled, email_override)) {
      const { error: dErr } = await supabase
        .from('revenue_collection_sales_manager_notify_rules')
        .delete()
        .eq('team_member_id', teamMemberId);
      if (dErr) throw dErr;
      return;
    }
    const { error } = await supabase.from('revenue_collection_sales_manager_notify_rules').upsert(
      {
        team_member_id: teamMemberId,
        notify_enabled,
        email_override,
        updated_at: now,
      },
      { onConflict: 'team_member_id' },
    );
    if (error) throw error;
  };

  const setNotifyEnabled = async (teamMemberId: number, enabled: boolean) => {
    setSectionError(null);
    const rawDraft = (overrideDrafts[teamMemberId] ?? '').trim();
    const email_override = rawDraft === '' ? null : rawDraft;
    try {
      await persistRule(teamMemberId, enabled, email_override);
      await loadAll();
    } catch (e) {
      setSectionError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const saveOverride = async (r: ManagerNotifyRow) => {
    const raw = (overrideDrafts[r.team_member_id] ?? '').trim();
    const email_override = raw === '' ? null : raw;
    const notify = effectiveNotifyEnabled(r);
    setSectionError(null);
    try {
      await persistRule(r.team_member_id, notify, email_override);
      await loadAll();
    } catch (e) {
      setSectionError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const resetToDefault = async (teamMemberId: number) => {
    if (!canMutateRevenue) return;
    if (!confirm('Reset this manager to default (notify on, use BackYou email)?')) return;
    setSectionError(null);
    try {
      const { error } = await supabase
        .from('revenue_collection_sales_manager_notify_rules')
        .delete()
        .eq('team_member_id', teamMemberId);
      if (error) throw error;
      await loadAll();
    } catch (e) {
      setSectionError(e instanceof Error ? e.message : 'Reset failed');
    }
  };

  const q = listFilter.trim().toLowerCase();
  const visibleRows =
    q === ''
      ? rows
      : rows.filter((r) => {
          const name = displayTeamMemberName(r.member).toLowerCase();
          const email = (r.member.email ?? '').toLowerCase();
          const idStr = String(r.team_member_id);
          return name.includes(q) || email.includes(q) || idStr.includes(q);
        });

  if (loading) {
    return (
      <div className="flex flex-1 min-h-0 items-center justify-center px-4 py-8 text-sm text-gray-500">
        Loading managers…
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 px-4 py-4">
      {sectionError ? (
        <div className="mb-3 shrink-0 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {sectionError}
        </div>
      ) : null}

      <div className="mb-4 flex shrink-0 flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex-1 min-w-0 max-w-md">
          <label htmlFor="sales-manager-notify-filter" className="block text-xs font-medium text-gray-600 mb-1">
            Filter list
          </label>
          <input
            id="sales-manager-notify-filter"
            type="search"
            value={listFilter}
            onChange={(e) => setListFilter(e.target.value)}
            placeholder="Name, email, or id…"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <p className="text-xs text-gray-500 sm:pb-2">
          {visibleRows.length} of {rows.length} manager{rows.length === 1 ? '' : 's'}
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="shrink-0 text-sm text-gray-500 py-2">No team members found in BackYou.</p>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Manager</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Default email</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notify</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Override To</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {visibleRows.map((r) => {
                const name = displayTeamMemberName(r.member) || `ID ${r.team_member_id}`;
                const defEmail = r.member.email?.trim() || '—';
                const notifyOn = effectiveNotifyEnabled(r);
                const hasCustom = r.explicit_rule != null;
                return (
                  <tr key={r.team_member_id}>
                    <td className="px-3 py-2 text-gray-900">{name}</td>
                    <td className="px-3 py-2 text-gray-600 tabular-nums break-all">{defEmail}</td>
                    <td className="px-3 py-2">
                      {hasCustom ? (
                        <span className="inline-flex rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 border border-amber-200">
                          Custom
                        </span>
                      ) : (
                        <span className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifyOn}
                          disabled={!canMutateRevenue}
                          onChange={(e) => void setNotifyEnabled(r.team_member_id, e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-gray-600">{notifyOn ? 'On' : 'Off'}</span>
                      </label>
                    </td>
                    <td className="px-3 py-2 min-w-[12rem]">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                        <input
                          type="email"
                          value={overrideDrafts[r.team_member_id] ?? ''}
                          onChange={(e) =>
                            setOverrideDrafts((prev) => ({
                              ...prev,
                              [r.team_member_id]: e.target.value,
                            }))
                          }
                          placeholder="Use default if empty"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          readOnly={!canMutateRevenue}
                        />
                        {canMutateRevenue ? (
                          <button
                            type="button"
                            onClick={() => void saveOverride(r)}
                            className="btn-xs-teal-outline whitespace-nowrap"
                          >
                            Save override
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canMutateRevenue && hasCustom ? (
                        <button
                          type="button"
                          onClick={() => void resetToDefault(r.team_member_id)}
                          className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-900"
                          aria-label="Reset to default"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Reset
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
